// src/pages/user/Favorites.js
import React, { useState, useEffect, useCallback } from 'react';
import { userAPI } from '../../services/api';
import DealCard from '../../components/deals/DealCard';
import Loading from '../../components/common/Loading';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

const Favorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);

  // 🔧 petite fonction util pour calculer le % de réduction
  const computeDiscount = (original, discounted) => {
    const orig = Number(original) || 0;
    const disc = Number(discounted) || 0;
    if (orig <= 0 || disc <= 0 || disc >= orig) return 0;
    return Math.round(((orig - disc) / orig) * 100);
  };

  const normalizeFavoriteDeal = (deal) => {
    if (!deal) return null;

    const discountedPrice =
      Number(deal.discountedPrice ?? deal.price ?? deal.discounted_price ?? 0);
    const originalPrice =
      Number(deal.originalPrice ?? deal.original_price ?? deal.priceBefore ?? deal.price ?? 0);

    const discountPercentage =
      deal.discountPercentage ??
      computeDiscount(originalPrice, discountedPrice);

    const quantityRemaining =
      deal.quantityRemaining ??
      deal.remaining ??
      deal.stock ??
      deal.quantity ??
      0;

    const endDate =
      deal.endDate ?? deal.validUntil ?? deal.endDateISO ?? deal.end ?? null;

    return {
      ...deal,
      discountedPrice,
      originalPrice,
      discountPercentage,
      quantityRemaining,
      endDate,
    };
  };

  const loadFavorites = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userAPI.getFavorites();

    
      let list = res?.data?.data;

      if (!Array.isArray(list)) {
        console.warn(' Favorites: data is not an array, got:', list);
        list = [];
      }

      // ⭐ ICI : on normalise chaque deal favori
      const normalized = list
        .map(normalizeFavoriteDeal)
        .filter(Boolean);

      console.log(' Favorites normalized:', normalized);

      setFavorites(normalized);
    } catch (error) {
      console.error('Erreur chargement favoris:', error);
      toast.error('Impossible de charger les favoris. Réessayez plus tard.');
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!mounted) return;
      await loadFavorites();
    })();
    return () => {
      mounted = false;
    };
  }, [loadFavorites]);

  const handleRemoveFavorite = async (dealId) => {
    if (!window.confirm('Supprimer ce deal de vos favoris ?')) return;
    try {
      setRemovingId(dealId);
      // backend supprime par ID DU DEAL: /api/users/favorites/:dealId
      await userAPI.removeFavorite(dealId);

      setFavorites((prev) =>
        prev.filter((d) => (d._id || d.id)?.toString() !== dealId.toString())
      );

      toast.success('Favori supprimé');
    } catch (err) {
      console.error('remove favorite error', err);
      toast.error('Impossible de supprimer le favori');
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) return <Loading />;

  if (!favorites || favorites.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <p className="text-gray-600 text-lg mb-4">
          Vous n&apos;avez pas encore de deals favoris
        </p>
        <p className="text-gray-500 mb-6">
          Explorez nos deals et ajoutez-les à vos favoris en cliquant sur le cœur ❤️
        </p>
        <Link
          to="/deals"
          className="inline-block px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Parcourir les deals
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Mes Favoris</h2>
        <button
          onClick={loadFavorites}
          className="text-sm px-3 py-1 border rounded hover:bg-gray-50"
          title="Rafraîchir"
        >
          Rafraîchir
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {favorites.map((deal) => {
          const id = (deal._id || deal.id || '').toString();
          return (
            <div key={id} className="relative">
              <DealCard deal={deal} />
              <button
                onClick={() => handleRemoveFavorite(id)}
                disabled={removingId === id}
                className="absolute top-3 right-3 bg-white p-1 rounded-full shadow hover:bg-gray-50"
                title="Retirer des favoris"
                aria-label={`Retirer ${deal.title} des favoris`}
              >
                {removingId === id ? '...' : '✖'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Favorites;
